/**
 * LRU Cache - Least Recently Used cache with TTL support
 *
 * Features:
 * - LRU eviction when max entries reached
 * - TTL expiration per entry
 * - Hit/miss statistics
 * - Prefix-based deletion for invalidation
 */

import type { CacheStats } from '@gzl10/nexus-sdk'
export type { CacheStats }

interface CacheEntry<T> {
  data: T
  expires: number
}

export interface LRUCacheOptions {
  /** Maximum number of entries (default: 100) */
  maxEntries?: number
  /** Default TTL in seconds (default: 60) */
  defaultTTL?: number
}

export class LRUCache<T = unknown> {
  private cache: Map<string, CacheEntry<T>>
  private readonly maxEntries: number
  private readonly defaultTTL: number

  private hits = 0
  private misses = 0

  constructor(options?: LRUCacheOptions) {
    this.cache = new Map()
    this.maxEntries = options?.maxEntries ?? 100
    this.defaultTTL = options?.defaultTTL ?? 60
  }

  /**
   * Get value from cache
   * Returns null if not found or expired
   * Moves entry to end (most recently used)
   */
  get(key: string): T | null {
    const entry = this.cache.get(key)

    if (!entry) {
      this.misses++
      return null
    }

    // Check expiration
    if (entry.expires > 0 && entry.expires < Date.now()) {
      this.cache.delete(key)
      this.misses++
      return null
    }

    // Move to end (most recently used)
    this.cache.delete(key)
    this.cache.set(key, entry)

    this.hits++
    return entry.data
  }

  /**
   * Set value in cache
   * Evicts oldest entry if max entries reached
   * @param ttl TTL in seconds (0 = no expiration, undefined = use default)
   */
  set(key: string, data: T, ttl?: number): void {
    // Delete existing to update position
    this.cache.delete(key)

    // Evict oldest if at capacity
    if (this.cache.size >= this.maxEntries) {
      const oldestKey = this.cache.keys().next().value
      if (oldestKey) {
        this.cache.delete(oldestKey)
      }
    }

    const actualTTL = ttl ?? this.defaultTTL
    const expires = actualTTL > 0 ? Date.now() + actualTTL * 1000 : 0

    this.cache.set(key, { data, expires })
  }

  /**
   * Delete specific key
   */
  delete(key: string): boolean {
    return this.cache.delete(key)
  }

  /**
   * Delete all keys matching prefix
   * @returns Number of deleted entries
   */
  deleteByPrefix(prefix: string): number {
    let deleted = 0
    for (const key of this.cache.keys()) {
      if (key.startsWith(prefix)) {
        this.cache.delete(key)
        deleted++
      }
    }
    return deleted
  }

  /**
   * Clear all entries
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key)
    if (!entry) return false
    if (entry.expires > 0 && entry.expires < Date.now()) {
      this.cache.delete(key)
      return false
    }
    return true
  }

  /**
   * Get current size
   */
  get size(): number {
    return this.cache.size
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    const total = this.hits + this.misses
    return {
      hits: this.hits,
      misses: this.misses,
      hitRate: total > 0 ? this.hits / total : 0,
      size: this.cache.size,
      maxEntries: this.maxEntries
    }
  }

  /**
   * Reset statistics
   */
  resetStats(): void {
    this.hits = 0
    this.misses = 0
  }

  /**
   * Prune expired entries
   * Call periodically to clean up memory
   */
  prune(): number {
    const now = Date.now()
    let pruned = 0

    for (const [key, entry] of this.cache.entries()) {
      if (entry.expires > 0 && entry.expires < now) {
        this.cache.delete(key)
        pruned++
      }
    }

    return pruned
  }
}
